import { Beech } from './beech';
import type { ActiveTab, HeadsetState, RecentCall } from './model';

type AnyBool = boolean | 'true' | 'false';

export class Persist {
	private beech: Beech;

	constructor(beech: Beech) {
		this.beech = beech;

		let data = beech.model.getData();
		for (let key in localStorage) {
			if (key.startsWith('settings:')) {
				data.persist[key.slice(9)] = localStorage.getItem(key);
			}
		}
	}

	public getNotifyIncomingCalls = (): boolean => {
		return this.readBoolean('notifyIncomingCalls', true);
	};

	public setNotifyIncomingCalls = (value: AnyBool) => {
		this.writeBoolean('notifyIncomingCalls', value);
	};

	public getNotifyLowAgents = (): boolean => {
		return this.readBoolean('notifyLowAgents', false);
	};

	public setNotifyLowAgents = (value: AnyBool) => {
		this.writeBoolean('notifyLowAgents', value);
	};

	public getNotifyOutgoingProtoCalls = (): boolean => {
		return this.readBoolean('notifyOutgoingProtoCalls', true);
	};

	public setNotifyOutgoingProtoCalls = (value: AnyBool) => {
		this.writeBoolean('notifyOutgoingProtoCalls', value);
	};

	public getNotifyQualityWarnings = (): boolean => {
		return this.readBoolean('notifyQualityWarnings', false);
	};

	public setNotifyQualityWarnings = (value: AnyBool) => {
		this.writeBoolean('notifyQualityWarnings', value);
	};

	public getTrackCallMetrics = (): boolean => {
		return this.readBoolean('trackCallMetrics', false);
	};

	public setTrackCallMetrics = (value: AnyBool) => {
		this.writeBoolean('trackCallMetrics', value);
	};

	public getForwardTwilioInsights = (): boolean => {
		return this.readBoolean('forwardTwilioInsights', true);
	};

	public setForwardTwilioInsights = (value: AnyBool) => {
		this.writeBoolean('forwardTwilioInsights', value);
	};

	public getAllowExternalInvites = (): boolean => {
		return this.readBoolean('allowExternalInvites', true);
	};

	public setAllowExternalInvites = (value: AnyBool) => {
		this.writeBoolean('allowExternalInvites', value);
	};

	public getAlertViewActive = (): boolean => {
		return this.readBoolean('alertViewActive', false);
	};

	public setAlertViewActive = (value: AnyBool) => {
		this.writeBoolean('alertViewActive', value);
	};

	public getSpeakerDevice = (): string | null => {
		return this.readString('outputDevice', null); // todo rename
	};

	public setSpeakerDevice = (deviceId: string) => {
		this.writeString('outputDevice', deviceId);
	};

	public getRingDevice = (): string | null => {
		return this.readString('ringDevice', null);
	};

	public setRingDevice = (deviceId: string) => {
		this.writeString('ringDevice', deviceId);
	};

	public getInputDevice = (): string | null => {
		return this.readString('inputDevice', null);
	};

	public setInputDevice = (deviceId: string) => {
		this.writeString('inputDevice', deviceId);
	};

	public clearCallerId = () => {
		logg.log('persist >> clear(callerId)');
		delete this.beech.model.getData().persist['callerId'];
		localStorage.removeItem('settings:callerId');
	};

	public getUseDevBanyan = (): boolean => {
		return this.readBoolean('useDevBanyan', false);
	};

	public setUseDevBanyan = (value: AnyBool) => {
		this.writeBoolean('useDevBanyan', value);
	};

	public getProtoSecret = () => {
		return this.readString('protoSecret', this.beech.util.createRandomString(20));
	};

	public getReconnectToken = (): string | null => {
		const raw = sessionStorage.getItem('reconnectToken');
		if (raw === null) return null;
		try {
			const parsed = JSON.parse(raw) as { token: string; expiresAt: number };
			if (Date.now() > parsed.expiresAt) {
				this.clearReconnectToken();
				return null;
			}
			return parsed.token;
		} catch {
			this.clearReconnectToken();
			return null;
		}
	};

	public setReconnectToken = (token: string) => {
		const TWELVE_HOURS_MS = 12 * 60 * 60 * 1000;
		const payload = JSON.stringify({ token, expiresAt: Date.now() + TWELVE_HOURS_MS });
		logg.log('persist >> write(reconnectToken, <12h>)');
		sessionStorage.setItem('reconnectToken', payload);
	};

	public clearReconnectToken = () => {
		sessionStorage.removeItem('reconnectToken');
	};

	public getLastEmail = (): string => {
		return localStorage.getItem('lastEmail') ?? '';
	};

	public setLastEmail = (email: string) => {
		localStorage.setItem('lastEmail', email);
	};

	public getLastDeviceRecoveryAt = (): number => {
		return Number(localStorage.getItem('deviceRecoveryAt')) || 0;
	};

	public setLastDeviceRecoveryAt = (ms: number) => {
		localStorage.setItem('deviceRecoveryAt', String(ms));
	};

	// Durable "remembered login" credential ("Session <guid>:<secret>"). Stored ONLY
	// in the OS keychain via Electron safeStorage. In browser/embedded mode there is
	// no keychain, so we deliberately do NOT persist it — a plaintext credential in
	// localStorage would be readable by any script or other user of the profile.
	// Browser users rely on the browser's own password manager instead.
	private static readonly SESSION_TOKEN_KEY = 'sessionToken';

	public setSessionToken = async (token: string): Promise<void> => {
		if (typeof window.electronAPI?.secureSet !== 'function') return;
		const ok = await window.electronAPI.secureSet(Persist.SESSION_TOKEN_KEY, token);
		if (!ok) {
			logg.warn('persist >> session token NOT saved — OS keychain unavailable; remembered login disabled');
		}
	};

	public getSessionToken = async (): Promise<string | null> => {
		if (typeof window.electronAPI?.secureGet !== 'function') return null;
		return await window.electronAPI.secureGet(Persist.SESSION_TOKEN_KEY);
	};

	public clearSessionToken = async (): Promise<void> => {
		if (typeof window.electronAPI?.secureDelete !== 'function') return;
		await window.electronAPI.secureDelete(Persist.SESSION_TOKEN_KEY);
	};

	// Per-user keys: scoped by username so multiple agents on the same browser
	// (e.g. three tabs with three different logins) don't share state.
	private userKey(name: string): string | null {
		const username = this.beech.model.getData().username;
		return username ? `settings:${name}:${username}` : null;
	}

	public getActiveTab = (): ActiveTab | null => {
		const key = this.userKey('activeTab');
		if (!key) return null;
		const raw = localStorage.getItem(key);
		if (raw === 'home' || raw === 'keypad' || raw === 'team' || raw === 'settings') {
			return raw;
		}
		return null;
	};

	public setActiveTab = (tab: ActiveTab) => {
		const key = this.userKey('activeTab');
		if (!key) return;
		localStorage.setItem(key, tab);
	};

	public getHeadsetState = (): HeadsetState | null => {
		const key = this.userKey('headsetState');
		if (!key) return null;
		const raw = localStorage.getItem(key);
		if (raw === 'IDLE' || raw === 'AVAILABLE' || raw === 'AWAY') {
			return raw;
		}
		return null;
	};

	public setHeadsetState = (state: HeadsetState) => {
		const key = this.userKey('headsetState');
		if (!key) return;
		localStorage.setItem(key, state);
	};

	public getRecentCalls = (): RecentCall[] | null => {
		const key = this.userKey('recentCalls');
		if (!key) return null;
		const raw = localStorage.getItem(key);
		if (raw === null) return null;
		try {
			const parsed = JSON.parse(raw);
			return Array.isArray(parsed) ? parsed : null;
		} catch {
			localStorage.removeItem(key);
			return null;
		}
	};

	public setRecentCalls = (calls: RecentCall[]) => {
		const key = this.userKey('recentCalls');
		if (!key) return;
		localStorage.setItem(key, JSON.stringify(calls));
	};

	public getLastDialed = (): string => {
		const key = this.userKey('lastDialed');
		if (!key) return '';
		return localStorage.getItem(key) ?? '';
	};

	public setLastDialed = (number: string) => {
		const key = this.userKey('lastDialed');
		if (!key) return;
		localStorage.setItem(key, number);
	};

	private readBoolean(key: string, defaultValue: boolean): boolean {
		let value = localStorage.getItem(`settings:${key}`);
		return value === null ? defaultValue : value == 'true';
	}

	private writeBoolean(key: string, value: AnyBool) {
		let boolValue = value === 'true' || value === true ? 'true' : 'false';

		logg.log('persist >> write(' + key + ', ' + boolValue + ')');

		this.beech.model.getData().persist[key] = boolValue;

		localStorage.setItem(`settings:${key}`, boolValue);
	}

	private readString(key: string, defaultValue: string | null): string | null {
		let value = localStorage.getItem(`settings:${key}`);
		return value === null ? defaultValue : value;
	}

	private writeString(key: string, value: string) {
		logg.log('persist >> write(' + key + ', "' + value + '")');

		this.beech.model.getData().persist[key] = value;

		localStorage.setItem(`settings:${key}`, value);
	}
}
